import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

/**
 * ConfirmEmailBanner - Soft email-confirmation nudge
 *
 * Shown to signed-in email-provider users whose address is not yet
 * confirmed. Part of the soft confirmation gate: signup no longer blocks on
 * the confirmation email, so this banner is the gentle follow-up that keeps
 * deliverability honest without stopping anyone at the door.
 *
 * - Session-dismissible per user (comes back next session until confirmed)
 * - Resend uses the existing verification email flow with sent-state feedback
 * - Slim, calm, and JP-token themed; never alarming
 */

const DISMISS_STORAGE_PREFIX = 'jp:confirm_email_banner_dismissed:';

type ResendState = 'idle' | 'sending' | 'sent' | 'error';

function readDismissed(userId: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.sessionStorage.getItem(`${DISMISS_STORAGE_PREFIX}${userId}`) === '1';
  } catch {
    return false;
  }
}

export function ConfirmEmailBanner({ className }: { className?: string }) {
  const { user, resendVerificationEmail } = useAuth();
  const [dismissed, setDismissed] = React.useState(false);
  const [resendState, setResendState] = React.useState<ResendState>('idle');

  const userId = user?.id ?? null;

  React.useEffect(() => {
    if (!userId) return;
    setDismissed(readDismissed(userId));
    setResendState('idle');
  }, [userId]);

  const isUnconfirmedEmailUser = Boolean(
    user &&
      !user.email_confirmed_at &&
      user.email &&
      (user.app_metadata?.provider ?? 'email') === 'email'
  );

  if (!user || !isUnconfirmedEmailUser || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(`${DISMISS_STORAGE_PREFIX}${user.id}`, '1');
    } catch {
      // Session-only dismissal still works via state.
    }
  };

  const handleResend = async () => {
    if (resendState === 'sending' || resendState === 'sent') return;
    setResendState('sending');
    const { error } = await resendVerificationEmail(user.email as string);
    setResendState(error ? 'error' : 'sent');
  };

  return (
    <div
      role="status"
      className={cn(
        'fixed inset-x-0 z-40 px-4',
        'bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] md:bottom-6',
        'animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none',
        className
      )}
    >
      <div className="mx-auto flex max-w-xl items-center gap-3 rounded-xl border border-jp-line-edge bg-jp-bg-elevated px-4 py-2.5 shadow-lg">
        <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
          <span
            className={cn(
              'absolute inline-flex h-full w-full rounded-full bg-[#D1F41A] opacity-60',
              resendState === 'sent' ? '' : 'animate-ping motion-reduce:animate-none'
            )}
          />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[#D1F41A]" />
        </span>

        <p className="min-w-0 flex-1 truncate font-v7-body text-sm text-jp-fg-secondary">
          {resendState === 'sent' ? (
            <>Confirmation sent. Check your inbox at{' '}
              <span data-ph-mask className="text-jp-fg-primary">{user.email}</span>.
            </>
          ) : (
            <>Confirm your email:{' '}
              <span data-ph-mask className="text-jp-fg-primary">{user.email}</span>
            </>
          )}
        </p>

        {resendState !== 'sent' && (
          <button
            type="button"
            onClick={handleResend}
            disabled={resendState === 'sending'}
            className="shrink-0 font-v7-body text-sm font-semibold text-jp-accent-primary underline underline-offset-4 transition-opacity hover:opacity-80 disabled:opacity-50"
          >
            {resendState === 'sending'
              ? 'Sending...'
              : resendState === 'error'
                ? 'Try again'
                : 'Resend link'}
          </button>
        )}

        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss email confirmation reminder"
          className="shrink-0 rounded-md p-1 text-jp-fg-faint transition-colors hover:text-jp-fg-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent-primary"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
